import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Col, Form, Input, Radio, Row, Select, Space, Spin, Typography, message } from "antd";
import { Controller, useForm, type Control } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { useAppConfig } from "../../shared/config/AppConfigProvider";
import { capitalizeNameInput, formatPhoneInput } from "../../shared/utils/formatters";
import { customersApi, type CustomerInput } from "./customers-api";

const customerSchema = z.object({
  type: z.enum(["INDIVIDUAL", "BUSINESS"]),
  salutation: z.enum(["MR", "MS"]).optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  companyName: z.string().optional(),
  email: z.string().email().optional().or(z.literal("")),
  phone: z.string().min(3),
  alternatePhone: z.string().optional(),
  addressLine1: z.string().optional(),
  addressLine2: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  postalCode: z.string().optional(),
  preferredLocale: z.enum(["en", "zh"])
}).superRefine((value, context) => {
  if (value.type === "INDIVIDUAL" && (!value.firstName?.trim() || !value.lastName?.trim())) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["firstName"], message: "First and last name are required" });
  }
  if (value.type === "BUSINESS" && !value.companyName?.trim()) {
    context.addIssue({ code: z.ZodIssueCode.custom, path: ["companyName"], message: "Company name is required" });
  }
});

export function CustomerFormPage() {
  const { id } = useParams();
  const editing = Boolean(id);
  const { text } = useAppConfig();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const customer = useQuery({ queryKey: ["customer", id], queryFn: () => customersApi.get(id!), enabled: editing });
  const { control, handleSubmit, watch, reset, formState: { errors } } = useForm<CustomerInput>({
    resolver: zodResolver(customerSchema),
    defaultValues: { type: "INDIVIDUAL", salutation: undefined, phone: "", preferredLocale: "en" },
    values: customer.data ? {
      type: customer.data.type, salutation: customer.data.salutation ?? undefined,
      firstName: customer.data.firstName ?? "", lastName: customer.data.lastName ?? "",
      companyName: customer.data.companyName ?? "", email: customer.data.email ?? "", phone: customer.data.phone,
      alternatePhone: customer.data.alternatePhone ?? "", addressLine1: customer.data.addressLine1 ?? "",
      addressLine2: customer.data.addressLine2 ?? "", city: customer.data.city ?? "", state: customer.data.state ?? "",
      postalCode: customer.data.postalCode ?? "", preferredLocale: customer.data.preferredLocale as "en" | "zh"
    } : undefined
  });
  const mutation = useMutation({
    mutationFn: (value: CustomerInput) => editing ? customersApi.update(id!, value) : customersApi.create(value),
    onSuccess: result => {
      void queryClient.invalidateQueries({ queryKey: ["customers"] });
      messageApi.success(text.save);
      navigate(`/customers/${result.id}`, { replace: true });
    }
  });
  if (editing && customer.isLoading) return <Spin />;
  return <section className="form-page">
    {contextHolder}
    <div className="page-title"><div><Typography.Title level={2}>{editing ? text.editCustomer : text.newCustomer}</Typography.Title></div></div>
    {mutation.isError && <Alert type="error" showIcon message={(mutation.error as Error).message} />}
    <form onSubmit={handleSubmit(value => mutation.mutate(value))}>
      <Card title={text.customer}>
        <Row gutter={18}>
          <Col xs={24}><Controller name="type" control={control} render={({ field }) => <Form.Item><Radio.Group {...field} optionType="button" options={[{ label: text.individual, value: "INDIVIDUAL" }, { label: text.company, value: "BUSINESS" }]} /></Form.Item>} /></Col>
          {watch("type") === "INDIVIDUAL" ? <>
            <Col xs={24} md={9}><Field control={control} name="firstName" label={text.firstName} error={errors.firstName?.message} nameCase /></Col>
            <Col xs={24} md={9}><Field control={control} name="lastName" label={text.lastName} error={errors.lastName?.message} nameCase /></Col>
            <Col xs={24} md={6}><Controller name="salutation" control={control} render={({ field }) => <Form.Item label={text.salutation}><Select {...field} allowClear options={[{ label: text.mr, value: "MR" }, { label: text.ms, value: "MS" }]} /></Form.Item>} /></Col>
          </> : <Col xs={24}><Field control={control} name="companyName" label={text.company} error={errors.companyName?.message} /></Col>}
          <Col xs={24} md={12}><Field control={control} name="phone" label={text.phone} error={errors.phone?.message} phone /></Col>
          <Col xs={24} md={12}><Field control={control} name="alternatePhone" label="Alternate phone" phone /></Col>
          <Col xs={24} md={12}><Field control={control} name="email" label={text.email} error={errors.email?.message} /></Col>
          <Col xs={24} md={12}><Controller name="preferredLocale" control={control} render={({ field }) => <Form.Item label={text.language}><Select {...field} options={[{ label: "English", value: "en" }, { label: "中文", value: "zh" }]} /></Form.Item>} /></Col>
        </Row>
      </Card>
      <Card title="Address" className="form-section">
        <Row gutter={18}>
          <Col xs={24}><Field control={control} name="addressLine1" label="Address" /></Col>
          <Col xs={24}><Field control={control} name="addressLine2" label="Address line 2" /></Col>
          <Col xs={24} md={10}><Field control={control} name="city" label="City" /></Col>
          <Col xs={12} md={7}><Field control={control} name="state" label="State" /></Col>
          <Col xs={12} md={7}><Field control={control} name="postalCode" label="ZIP code" /></Col>
        </Row>
      </Card>
      <Space className="form-actions">
        <Button onClick={() => { reset(); navigate(-1); }}>{text.cancel}</Button>
        <Button type="primary" htmlType="submit" loading={mutation.isPending}>{text.save}</Button>
      </Space>
    </form>
  </section>;
}

function Field({ control, name, label, error, phone = false, nameCase = false }: { control: Control<CustomerInput>; name: keyof CustomerInput; label: string; error?: string; phone?: boolean; nameCase?: boolean }) {
  return <Controller name={name} control={control} render={({ field }) => <Form.Item label={label} validateStatus={error ? "error" : undefined} help={error}><Input
    {...field}
    inputMode={phone ? "tel" : undefined}
    autoComplete={phone ? "tel" : undefined}
    value={phone ? formatPhoneInput(String(field.value ?? "")) : String(field.value ?? "")}
    onChange={event => field.onChange(phone ? formatPhoneInput(event.target.value) : nameCase ? capitalizeNameInput(event.target.value) : event.target.value)}
  /></Form.Item>} />;
}
